import { useState } from 'react';

import { Button } from '../../../../../components/ui/Button';
import { Dialog } from '../../../../../components/ui/Dialog';
import { Table, type TableColumn } from '../../../../../components/ui/Table';
import { Tag } from '../../../../../components/ui/Tag';
import type { Coupon, CouponGroup } from '../fixtures';
import { deriveCouponStatus } from '../fixtures';
import { pairSources, relatedCoupons } from '../stackRules';
import styles from './CouponManagementPage.module.css';
import { couponSourceLabel, couponStatusTone, couponTypeTagCategory } from './couponPresentation';

interface ExampleRow {
  combination: string;
  ok: boolean;
  reason: string;
}

const exampleRows: ExampleRow[] = [
  { combination: '老带新券＋语文券', ok: true, reason: '两张券之间有关系' },
  { combination: '老带新券＋数学券', ok: true, reason: '两张券之间有关系' },
  { combination: '语文券＋数学券', ok: false, reason: '两张券之间没有关系' },
  { combination: '老带新券＋语文券＋数学券', ok: false, reason: '语文券与数学券之间没有关系' },
];

const exampleColumns: TableColumn<ExampleRow>[] = [
  { key: 'combination', title: '使用组合' },
  { key: 'ok', render: (row) => <Tag type={row.ok ? 'success' : 'neutral'}>{row.ok ? '可以' : '不可以'}</Tag>, title: '结果', width: 120 },
  { key: 'reason', title: '原因' },
];

export function RuleExampleDialog({ onOpenChange, open }: { onOpenChange: (open: boolean) => void; open: boolean }) {
  return <Dialog footer={<Button onClick={() => onOpenChange(false)}>我知道了</Button>} headerTone="brand" onOpenChange={onOpenChange} open={open} size="lg" title="叠加规则示例">
    <div className={styles.example}>
      <p className={styles.exampleIntro}>三种方式都是设置“两张券之间能不能一起用”的关系。多张券一起用时，任意两张之间都必须有关系，关系不会自动传递。</p>
      <div className={styles.exampleMethods}>
        <div className={styles.exampleMethod}><b>与全部优惠券叠加</b><p>例如：老带新券设置后，可以和语文券、数学券、英语券等所有优惠券一起用，未来新增的优惠券也自动包含。</p><div className={styles.exampleChips}><Tag>老带新券</Tag><Tag type="success">任意优惠券</Tag></div></div>
        <div className={styles.exampleMethod}><b>仅与指定券叠加</b><p>例如：老带新券只和勾选的语文券、数学券一起用，未勾选的美术券不能叠加。</p><div className={styles.exampleChips}><Tag>老带新券</Tag><Tag type="success">语文券</Tag><Tag type="success">数学券</Tag><Tag type="neutral">美术券 ×</Tag></div></div>
        <div className={styles.exampleMethod}><b>创建优惠券组合</b><p>例如：把语文券、数学券、英语券放进一个组合，组内任意券之间都能叠加，互相之间都会建立关系。</p><div className={styles.exampleChips}><Tag type="success">语文券</Tag><Tag type="success">数学券</Tag><Tag type="success">英语券</Tag></div></div>
      </div>
      <div className={styles.exampleRuleNotice}><b>关键规则：</b>三张券一起用，必须任意两张之间都有关系。</div>
      <div className={styles.relationDiagram}>
        <img alt="优惠券叠加关系示意" src="./assets/coupon-management/stacking-rule-example.png" />
      </div>
      <Table ariaLabel="叠加结果示例" columns={exampleColumns} rowKey={(row) => row.combination} rows={exampleRows} />
    </div>
  </Dialog>;
}

export function RelationDetailDialog({ coupon, entities, groups, onEdit, onOpenChange, open }: { coupon: Coupon | undefined; entities: readonly Coupon[]; groups: readonly CouponGroup[]; onEdit: (id: number) => void; onOpenChange: (open: boolean) => void; open: boolean }) {
  const [page, setPage] = useState(1);
  const related = coupon
    ? [...relatedCoupons(coupon.id, entities, groups)].sort((left, right) => Number(right.kind === 'recommendation') - Number(left.kind === 'recommendation'))
    : [];
  const hasRelated = related.length > 0;

  const columns: TableColumn<Coupon>[] = [
    { key: 'name', title: '可叠加优惠券', width: 190 },
    { key: 'couponSource', render: (other) => couponSourceLabel(other.source), title: '券来源', width: 100 },
    { key: 'type', render: (other) => other.kind === 'recommendation' ? null : <Tag category={couponTypeTagCategory(other.type)}>{other.type}</Tag>, title: '类型', width: 100 },
    {
      key: 'status',
      render: (other) => {
        if (other.kind === 'recommendation') return null;
        const current = deriveCouponStatus(other);
        const tone = couponStatusTone(current);
        const dotClass = tone === 'active' ? styles.statusDotActive : tone === 'warning' ? styles.statusDotWarning : styles.statusDotMuted;
        return <span className={styles.statusText}><span aria-hidden="true" className={dotClass} />{current}</span>;
      },
      title: '状态',
      width: 100,
    },
    { key: 'enabled', render: (other) => other.kind === 'recommendation' ? null : other.enabled ? '启用' : '停用', title: '启用/停用', width: 90 },
    { key: 'source', render: (other) => (coupon ? pairSources(coupon.id, other.id, entities, groups).join('；') : ''), title: '关系来源', width: 240 },
  ];

  return <Dialog
    footer={coupon && hasRelated ? <Button onClick={() => onOpenChange(false)}>知道了</Button> : undefined}
    headerTone="brand"
    onOpenChange={onOpenChange}
    open={open}
    size="md"
    title={coupon ? `${coupon.name}｜叠加范围详情` : '叠加范围详情'}
  >
    {hasRelated ? <Table
      ariaLabel="叠加范围详情"
      columns={columns}
      emptyText="暂无叠加范围"
      pagination={{ ariaLabel: '叠加范围详情分页', footerStart: <span>共 {related.length} 张优惠券</span>, onPageChange: setPage, page, pageSize: 10 }}
      rowKey={(other) => other.id}
      rows={related}
    /> : <div className={styles.relationEmpty}>
      <img
        alt=""
        aria-hidden="true"
        className={styles.relationEmptyIllustration}
        src="./assets/teacher-schedule/empty-classroom.png"
      />
      <strong>暂无叠加范围</strong>
      {coupon ? <Button onClick={() => onEdit(coupon.id)}>去设置</Button> : null}
    </div>}
  </Dialog>;
}
